import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Add,
  ViewList,
  ViewModule,
  ViewWeek,
  Edit,
  Payments
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { authGet, authPost, authPatch, authDelete } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { dateOnly, formatDate, parseDbDate, toDateInputValue } from '../../lib/dateFormat';
import { referralSourceOptionsFor } from '../../lib/referralSources';
import {
  buildAdditiveStudentEnrollmentPatch,
  buildCreatedStudentEnrollmentPatch,
  reconcileStudentSelectionAfterPersistedAdd
} from './studentEnrollmentPayload';
import { calendarDayCellSx } from './calendarDayLayout';
import { formatWeekRange, getWeekDays } from './calendarWeekLayout';
import { studentDisplayDetails } from './studentDisplayDetails';
import { appendNextMonth, startOfMonth as scrollStartOfMonth } from './continuousCalendarMonths';
import { mergeIntakeCalendarLegendClasses } from './intakeCalendarLegend';

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfCalendar(date) {
  const first = startOfMonth(date);
  const day = first.getDay();
  return new Date(first.getFullYear(), first.getMonth(), first.getDate() - day);
}

function endOfCalendar(date) {
  const last = endOfMonth(date);
  const day = last.getDay();
  return new Date(last.getFullYear(), last.getMonth(), last.getDate() + (6 - day));
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isDateInRange(day, start, end) {
  const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const startOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return dayOnly >= startOnly && dayOnly <= endOnly;
}

function formatMonthYear(date) {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
}

function getDayName(index) {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index];
}


const WEEKDAY_OPTIONS = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' }
];

const CLASS_STATUS_FILTER_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'locked', label: 'Locked' },
  { value: 'archived', label: 'Archived' }
];

function normalizeRecurrenceDays(value) {
  const values = Array.isArray(value) ? value : String(value || '').split(',');
  return [...new Set(values
    .map((item) => String(item).trim())
    .filter(Boolean)
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item >= 0 && item <= 6))].sort((a, b) => a - b);
}

function inferScheduleType(scheduledClass = {}) {
  if (normalizeRecurrenceDays(scheduledClass.recurrence_days).length) {
    return 'recurring';
  }
  const start = dateOnly(scheduledClass.class_date);
  const end = dateOnly(scheduledClass.end_date || scheduledClass.class_date);
  return start && end && start !== end ? 'date_range' : 'one_day';
}

function buildRecurringMeetingDates(startDate, endDate, recurrenceDays) {
  const start = parseDbDate(startDate);
  const end = parseDbDate(endDate || startDate);
  const days = normalizeRecurrenceDays(recurrenceDays);
  if (!start || !end || !days.length || end < start) return [];

  const result = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    if (days.includes(cursor.getDay())) result.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
}

function recurrenceLabel(row) {
  const days = normalizeRecurrenceDays(row?.recurrence_days);
  if (!days.length) return '';
  const dayLabel = days.map((day) => WEEKDAY_OPTIONS.find((item) => item.value === day)?.short).filter(Boolean).join(', ');
  const end = formatDate(row?.end_date || row?.class_date);
  return end ? `${dayLabel} through ${end}` : dayLabel;
}

function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function getSsnLast4(student) {
  const providedLast4 = String(student?.ssn_last4 || '').replace(/\D/g, '').slice(-4);
  if (providedLast4) return providedLast4;
  const ssnDigits = String(student?.ssn || '').replace(/\D/g, '');
  return ssnDigits ? ssnDigits.slice(-4) : '';
}

function formatSSNLast4(value) {
  return String(value || '').replace(/\D/g, '').slice(-4);
}

function formatMoney(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function emptyStudentForm() {
  return {
    code: '',
    ssn: '',
    last_name: '',
    first_name: '',
    middle_name: '',
    birth_date: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    call_notes: '',
    driver_number: '',

    cause_number: '',
    county_of_conviction: '',
    probation_officer_name: '',
    probation_officer_email: '',
    probation_officer_phone: '',
    attorney_name: '',
    attorney_email: '',
    attorney_phone: '',
    reporting_agency: '',
    referral_source: '',
    state_reporting_notes: ''
  };
}

function studentToForm(student = {}) {
  return {
    ...emptyStudentForm(),
    ...student,
    birth_date: dateOnly(student.birth_date),
    ssn: getSsnLast4(student)
  };
}

function studentPayloadFromForm(form = {}) {
  return {
    ...form,
    ssn: formatSSNLast4(form.ssn),
    birth_date: form.birth_date || null,
    cause_number: form.cause_number || '',
    probation_officer_phone: formatPhone(form.probation_officer_phone || ''),
    attorney_phone: formatPhone(form.attorney_phone || '')
  };
}


function readableTextColor(hex) {
  const raw = String(hex || '#1976D2').replace('#', '');
  if (raw.length !== 6) return '#fff';
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.68 ? '#111827' : '#fff';
}

const CLASS_COLOR_PALETTE = [
  '#A62305',
  '#D98219',
  '#0F766E',
  '#2563EB',
  '#7C3AED',
  '#DB2777',
  '#059669',
  '#B45309',
  '#4F46E5',
  '#DC2626',
  '#0891B2',
  '#65A30D'
];

function hashString(value) {
  return String(value || '').split('').reduce((hash, char) => {
    const next = ((hash << 5) - hash) + char.charCodeAt(0);
    return next | 0;
  }, 0);
}

function normalizeCalendarColor(value) {
  const raw = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw.toUpperCase() : '';
}

function getClassColor(item = {}, classColorById = {}) {
  const directColor = normalizeCalendarColor(item.calendar_color || item.calendarColor);
  if (directColor) return directColor;

  const classId = item.class_id || item.id;
  if (classId && classColorById[classId]) return classColorById[classId];

  const key = classId || item.class_code || item.code || item.class_name || item.name || 'class';
  const index = Math.abs(hashString(key)) % CLASS_COLOR_PALETTE.length;
  return CLASS_COLOR_PALETTE[index];
}

export default function CalendarPage({ enableRecurrence = false, showStatusFilter = false, defaultStatusFilter = [] } = {}) {
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedCalendarClassId, setSelectedCalendarClassId] = useState('');
  const [classStatusFilter, setClassStatusFilter] = useState(defaultStatusFilter);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [continuousMonths, setContinuousMonths] = useState(() => [scrollStartOfMonth(new Date())]);
  const calendarContinuationRef = useRef(null);

  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [addInstructorOpen, setAddInstructorOpen] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);

  const [studentSearch, setStudentSearch] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [selectedStudentObjects, setSelectedStudentObjects] = useState([]);
  const [originalStudentIds, setOriginalStudentIds] = useState([]);
  const [studentsDirty, setStudentsDirty] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'one_day',
    class_id: '',
    instructor_id: '',
    class_date: toDateInputValue(new Date()),
    class_time: '09:00',
    end_date: toDateInputValue(new Date()),
    end_time: '10:00',
    location: '',
    notes: '',
    recurrence_days: [],
    recurrence_weeks: null,
    status: 'open',
    admin_override: false,
    student_ids: []
  });

  const [newInstructor, setNewInstructor] = useState({
    code: '',
    name: '',
    active: true,
    phone: '',
    email: ''
  });

  const [newStudent, setNewStudent] = useState(emptyStudentForm());
  const [studentProfileOpen, setStudentProfileOpen] = useState(false);
  const [studentProfileForm, setStudentProfileForm] = useState(emptyStudentForm());
  const [studentProfileSaving, setStudentProfileSaving] = useState(false);
  const [studentProfileTarget, setStudentProfileTarget] = useState(null);
  const [studentPaymentOpen, setStudentPaymentOpen] = useState(false);
  const [studentPaymentTarget, setStudentPaymentTarget] = useState(null);
  const [studentPaymentForm, setStudentPaymentForm] = useState({ amount: '', payment_date: toDateInputValue(new Date()), method: '', reference_number: '', notes: '' });
  const [workflowStatus, setWorkflowStatus] = useState('');
  const [workflowError, setWorkflowError] = useState('');

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const statusQuery = showStatusFilter && classStatusFilter.length
        ? `?status=${encodeURIComponent(classStatusFilter.join(','))}`
        : '';
      const [classesData, instructorsData, scheduledData] = await Promise.all([
        authGet('/api/classes'),
        authGet('/api/instructors'),
        authGet(`/api/scheduled-classes${statusQuery}`)
      ]);

      setAvailableClasses(classesData.classes || []);
      setAvailableInstructors(instructorsData.instructors || []);
      setScheduledClasses(scheduledData.scheduledClasses || []);
    } catch (err) {
      console.error('Load calendar data error:', err);
    } finally {
      setLoading(false);
    }
  }, [classStatusFilter, showStatusFilter]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const searchStudents = async (query) => {
    const q = String(query || '').trim();

    if (!q) {
      setStudentOptions([]);
      return;
    }

    setStudentSearchLoading(true);
    try {
      const data = await authGet(`/api/students/search/list?q=${encodeURIComponent(q)}&limit=20`);
      setStudentOptions(data.students || []);
    } catch (err) {
      console.error('Search students error:', err);
      setStudentOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchStudents(studentSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [studentSearch]);

  const calendarDaysForMonth = useCallback((month) => {
    const start = startOfCalendar(month);
    const end = endOfCalendar(month);
    const days = [];
    const current = new Date(start);

    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, []);

  const continuousCalendarMonths = useMemo(() => continuousMonths.map((month) => ({
    month,
    days: calendarDaysForMonth(month)
  })), [calendarDaysForMonth, continuousMonths]);

  useEffect(() => {
    setContinuousMonths([scrollStartOfMonth(currentMonth)]);
  }, [currentMonth]);

  const appendContinuousMonth = useCallback(() => {
    setContinuousMonths((months) => appendNextMonth(months));
  }, []);

  useEffect(() => {
    if (viewMode !== 'calendar' || !calendarContinuationRef.current || !window.IntersectionObserver) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) appendContinuousMonth();
    }, { rootMargin: '240px 0px' });
    observer.observe(calendarContinuationRef.current);
    return () => observer.disconnect();
  }, [appendContinuousMonth, continuousCalendarMonths.length, viewMode]);

  const weekDays = useMemo(() => getWeekDays(currentMonth), [currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handlePrevWeek = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), currentMonth.getDate() - 7));
  };

  const handleNextWeek = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), currentMonth.getDate() + 7));
  };

  const resetScheduleForm = () => {
    const baseDate = toDateInputValue(new Date());
    setScheduleForm({
      schedule_type: 'one_day',
      class_id: availableClasses[0]?.id || '',
      instructor_id: '',
      class_date: baseDate,
      class_time: '09:00',
      end_date: baseDate,
      end_time: '10:00',
      location: '',
      notes: '',
      student_ids: []
    });
    setSelectedStudentObjects([]);
    setOriginalStudentIds([]);
    setStudentsDirty(false);
    setStudentSearch('');
    setStudentOptions([]);
    setEditing(null);
  };

  const openScheduleDialog = () => {
    const baseDate = toDateInputValue(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
    setScheduleForm({
      schedule_type: 'one_day',
      class_id: availableClasses[0]?.id || '',
      instructor_id: '',
      class_date: baseDate,
      class_time: '09:00',
      end_date: baseDate,
      end_time: '10:00',
      location: '',
      notes: '',
      student_ids: []
    });
    setSelectedStudentObjects([]);
    setOriginalStudentIds([]);
    setStudentsDirty(false);
    setStudentSearch('');
    setStudentOptions([]);
    setEditing(null);
    setScheduleOpen(true);
  };

  const handleEditScheduledClass = async (row) => {
    try {
      const detail = await authGet(`/api/scheduled-classes/${row.id}`);
      setScheduleForm({
        schedule_type: inferScheduleType(detail.scheduledClass),
        class_id: detail.scheduledClass.class_id || '',
        instructor_id: detail.scheduledClass.instructor_id || '',
        class_date: dateOnly(detail.scheduledClass.class_date),
        class_time: String(detail.scheduledClass.class_time).slice(0, 5),
        end_date: dateOnly(detail.scheduledClass.end_date || detail.scheduledClass.class_date),
        end_time: String(detail.scheduledClass.end_time || detail.scheduledClass.class_time).slice(0, 5),
        location: detail.scheduledClass.location || '',
        notes: detail.scheduledClass.notes || '',
        recurrence_days: normalizeRecurrenceDays(detail.scheduledClass.recurrence_days),
        recurrence_weeks: detail.scheduledClass.recurrence_weeks || null,
        status: detail.scheduledClass.status || 'open',
        admin_override: false,
        student_ids: (detail.students || []).map((s) => s.id)
      });
      const loadedStudents = detail.students || [];
      setSelectedStudentObjects(loadedStudents);
      setOriginalStudentIds(loadedStudents.map((student) => student.id));
      setStudentsDirty(false);
      setEditing(row);
      setScheduleOpen(true);
    } catch (err) {
      console.error('Load scheduled class detail error:', err);
    }
  };

  const handleSaveScheduledClass = async () => {
    try {
      const isRecurringSchedule = enableRecurrence && scheduleForm.schedule_type === 'recurring';
      const isOneDaySchedule = enableRecurrence && scheduleForm.schedule_type === 'one_day';
      const payload = {
        ...scheduleForm,
        end_date: isOneDaySchedule ? scheduleForm.class_date : scheduleForm.end_date,
        instructor_id: scheduleForm.instructor_id || null,
        ...(isRecurringSchedule
          ? {
              recurrence_days: scheduleForm.recurrence_days,
              recurrence_weeks: null
            }
          : {})
      };

      delete payload.schedule_type;

      if (editing) {
        delete payload.student_ids;
        if (studentsDirty) {
          Object.assign(payload, buildAdditiveStudentEnrollmentPatch({
            originalStudentIds,
            selectedStudentIds: selectedStudentObjects.map((student) => student.id)
          }));
        }
      }

      if (!isRecurringSchedule) {
        payload.recurrence_days = [];
        payload.recurrence_weeks = null;
      }

      if (editing) {
        await authPatch(`/api/scheduled-classes/${editing.id}`, payload);
      } else {
        await authPost('/api/scheduled-classes', payload);
      }

      setScheduleOpen(false);
      resetScheduleForm();
      await loadAllData();
    } catch (err) {
      console.error('Save scheduled class error:', err);
    }
  };

  const handleDeleteScheduledClass = async (id) => {
    if (!window.confirm('Delete this scheduled class?')) return;

    try {
      await authDelete(`/api/scheduled-classes/${id}`, { admin_override: true });
      await loadAllData();
    } catch (err) {
      console.error('Delete scheduled class error:', err);
      alert(err.message || 'Unable to delete scheduled class.');
    }
  };

  const handleCreateInstructor = async () => {
    try {
      const data = await authPost('/api/instructors', newInstructor);
      const instructor = data.instructor;

      setAddInstructorOpen(false);
      setNewInstructor({
        code: '',
        name: '',
        active: true,
        phone: '',
        email: ''
      });

      await loadAllData();

      if (instructor?.id) {
        setScheduleForm((prev) => ({
          ...prev,
          instructor_id: instructor.id
        }));
      }
    } catch (err) {
      console.error('Create instructor error:', err);
    }
  };

  const handleCreateStudent = async () => {
    try {
      const data = await authPost('/api/students', studentPayloadFromForm(newStudent));
      const student = data.student;

      setAddStudentOpen(false);
      setNewStudent(emptyStudentForm());

      if (student?.id) {
        const studentObj = {
          id: student.id,
          code: student.code,
          first_name: student.first_name,
          middle_name: student.middle_name,
          last_name: student.last_name,
          ssn_last4: getSsnLast4(student),
          birth_date: student.birth_date,
          address: student.address,
          city: student.city,
          state: student.state,
          zip: student.zip,
          phone: student.phone,
          email: student.email,
          driver_number: student.driver_number,

          cause_number: student.cause_number,
          county_of_conviction: student.county_of_conviction,
          probation_officer_name: student.probation_officer_name,
          probation_officer_email: student.probation_officer_email,
          probation_officer_phone: student.probation_officer_phone,
          attorney_name: student.attorney_name,
          attorney_email: student.attorney_email,
          attorney_phone: student.attorney_phone,
          reporting_agency: student.reporting_agency,
          referral_source: student.referral_source,
          state_reporting_notes: student.state_reporting_notes
        };

        const nextSelected = [...selectedStudentObjects.filter((item) => item.id !== student.id), studentObj];
        const nextStudentIds = nextSelected.map((item) => item.id);

        setSelectedStudentObjects(nextSelected);
        setStudentsDirty(true);
        setScheduleForm((prev) => ({
          ...prev,
          student_ids: nextStudentIds
        }));

        if (editing?.id) {
          await authPatch(
            `/api/scheduled-classes/${editing.id}`,
            buildCreatedStudentEnrollmentPatch(student.id)
          );

          const detail = await authGet(`/api/scheduled-classes/${editing.id}`);
          const refreshedStudents = detail.students || [];
          const reconciled = reconcileStudentSelectionAfterPersistedAdd({
            refreshedStudents,
            originalStudentIds,
            selectedStudents: nextSelected
          });
          setSelectedStudentObjects(reconciled.selectedStudents);
          setOriginalStudentIds(reconciled.authoritativeStudentIds);
          setStudentsDirty(reconciled.isDirty);
          setScheduleForm((prev) => ({
            ...prev,
            student_ids: reconciled.selectedStudents.map((item) => item.id)
          }));
          await loadAllData();
        }
      }
    } catch (err) {
      console.error('Create student error:', err);
      alert(err.message || 'Unable to save student. Please confirm first name is filled in and try again.');
    }
  };


  const refreshSelectedStudent = (student) => {
    if (!student?.id) return;
    const normalized = {
      ...student,
      ssn_last4: getSsnLast4(student)
    };
    setSelectedStudentObjects((prev) => prev.map((item) => item.id === student.id ? { ...item, ...normalized } : item));
  };

  const openStudentProfile = async (student) => {
    if (!student?.id) return;
    setWorkflowError('');
    setWorkflowStatus('');
    try {
      const data = await authGet(`/api/students/${student.id}`);
      setStudentProfileTarget(data.student || student);
      setStudentProfileForm(studentToForm(data.student || student));
      setStudentProfileOpen(true);
    } catch (err) {
      setWorkflowError(err.message || 'Unable to load student profile');
    }
  };

  const saveStudentProfile = async () => {
    if (!studentProfileTarget?.id) return;
    setStudentProfileSaving(true);
    setWorkflowError('');
    setWorkflowStatus('');
    try {
      const data = await authPatch(`/api/students/${studentProfileTarget.id}`, studentPayloadFromForm(studentProfileForm));
      refreshSelectedStudent(data.student);
      setStudentProfileTarget(data.student);
      setStudentProfileForm(studentToForm(data.student));
      setWorkflowStatus('Student profile updated.');
      setStudentProfileOpen(false);
      await loadAllData();
    } catch (err) {
      setWorkflowError(err.message || 'Unable to save student profile');
    } finally {
      setStudentProfileSaving(false);
    }
  };

  const openStudentPayment = (student) => {
    if (!student?.id) return;
    setWorkflowError('');
    setWorkflowStatus('');
    setStudentPaymentTarget(student);
    setStudentPaymentForm({
      amount: '',
      payment_date: toDateInputValue(new Date()),
      method: '',
      reference_number: '',
      notes: editing?.class_name ? `Calendar intake payment for ${editing.class_name}` : 'Calendar intake payment'
    });
    setStudentPaymentOpen(true);
  };

  const saveStudentPayment = async () => {
    if (!studentPaymentTarget?.id) return;
    const amount = Number(studentPaymentForm.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setWorkflowError('Enter a payment amount greater than $0.');
      return;
    }
    setWorkflowError('');
    setWorkflowStatus('');
    try {
      await authPost('/api/payments', {
        student_id: studentPaymentTarget.id,
        scheduled_class_id: editing?.id || null,
        amount,
        payment_date: studentPaymentForm.payment_date || null,
        method: studentPaymentForm.method || '',
        reference_number: studentPaymentForm.reference_number || '',
        notes: studentPaymentForm.notes || 'Calendar intake payment'
      });
      setWorkflowStatus(`Payment recorded for ${studentPaymentTarget.first_name || ''} ${studentPaymentTarget.last_name || ''}`.trim());
      setSelectedStudentObjects((prev) => prev.map((student) => (
        student.id === studentPaymentTarget.id
          ? {
              ...student,
              has_payment: true,
              payment_count: Number(student.payment_count || 0) + 1,
              payment_total: Number(student.payment_total || 0) + amount
            }
          : student
      )));
      setStudentPaymentOpen(false);
      await loadAllData();
    } catch (err) {
      setWorkflowError(err.message || 'Unable to record payment');
    }
  };

  const classColorById = useMemo(() => {
    const map = {};
    availableClasses.forEach((item) => {
      const savedColor = normalizeCalendarColor(item.calendar_color);
      if (item.id && savedColor) map[item.id] = savedColor;
    });
    return map;
  }, [availableClasses]);

  const activeInstructors = useMemo(
    () => availableInstructors.filter((item) => item?.active !== false),
    [availableInstructors]
  );

  const calendarEvents = useMemo(
    () =>
      scheduledClasses
        .flatMap((item) => {
          const calendarColor = getClassColor(item, classColorById);
          const baseEvent = {
            ...item,
            calendarColor,
            calendarTextColor: readableTextColor(calendarColor),
            recurrence_label: recurrenceLabel(item)
          };

          if (enableRecurrence) {
            const meetingDates = buildRecurringMeetingDates(item.class_date, item.end_date || item.class_date, item.recurrence_days);
            if (meetingDates.length) {
              return meetingDates.map((meetingDate) => ({
                ...baseEvent,
                id: item.id,
                occurrenceKey: `${item.id}-${toDateInputValue(meetingDate)}`,
                startDateObj: meetingDate,
                endDateObj: meetingDate,
                occurrence_date: toDateInputValue(meetingDate)
              }));
            }
          }

          const startDateObj = parseDbDate(item.class_date);
          const endDateObj = parseDbDate(item.end_date || item.class_date);
          if (!startDateObj || !endDateObj) return [];
          return [{ ...baseEvent, occurrenceKey: `${item.id}-range`, startDateObj, endDateObj }];
        })
        .filter(Boolean),
    [scheduledClasses, enableRecurrence, classColorById]
  );

  const calendarLegendClasses = useMemo(() => {
    const availableById = new Map(
      availableClasses
        .filter((item) => item?.id)
        .map((item) => [String(item.id), item])
    );
    const visibleStart = viewMode === 'week' ? weekDays[0] : startOfMonth(currentMonth);
    const visibleEnd = viewMode === 'week' ? weekDays[6] : endOfMonth(currentMonth);
    const seen = new Set();
    const legend = [];

    calendarEvents.forEach((event) => {
      if (event.endDateObj < visibleStart || event.startDateObj > visibleEnd) return;

      const classId = event.class_id || event.id;
      if (!classId) return;
      const key = String(classId);
      if (seen.has(key)) return;
      seen.add(key);

      const classRecord = availableById.get(key) || {};
      legend.push({
        ...classRecord,
        id: classId,
        code: classRecord.code || event.class_code || '',
        name: event.class_name || classRecord.name || event.class_code || 'Class',
        calendar_color: event.calendar_color || classRecord.calendar_color
      });
    });

    return mergeIntakeCalendarLegendClasses(legend, availableClasses);
  }, [availableClasses, calendarEvents, currentMonth, viewMode, weekDays]);

  useEffect(() => {
    if (!selectedCalendarClassId) return;
    const selectedClassIsVisible = calendarLegendClasses.some((item) => item.id === selectedCalendarClassId);
    if (!selectedClassIsVisible) setSelectedCalendarClassId('');
  }, [calendarLegendClasses, selectedCalendarClassId]);

  const listColumns = [
    { field: 'class_code', headerName: 'Code', flex: 0.7 },
    { field: 'class_name', headerName: 'Class', flex: 1.2 },
    { field: 'class_date', headerName: 'Start Date', flex: 1, renderCell: (params) => formatDate(params.row.class_date) },
    {
      field: 'class_time',
      headerName: 'Start Time',
      flex: 1,
      renderCell: (params) => formatTimeLabel(params.row.class_time)
    },
    { field: 'end_date', headerName: 'End Date', flex: 1, renderCell: (params) => formatDate(params.row.end_date || params.row.class_date) },
    {
      field: 'end_time',
      headerName: 'End Time',
      flex: 1,
      renderCell: (params) => formatTimeLabel(params.row.end_time)
    },
    ...(enableRecurrence ? [{ field: 'recurrence_label', headerName: 'Occurs', flex: 1, valueGetter: (_, row = {}) => recurrenceLabel(row) }] : []),
    { field: 'instructor_name', headerName: 'Instructor', flex: 1.2 },
    { field: 'location', headerName: 'Location', flex: 1 },
    { field: 'student_count', headerName: 'Students', flex: 0.8 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
          <Button size="small" variant="outlined" onClick={() => handleEditScheduledClass(params.row)}>
            Edit
          </Button>
          <Button size="small" color="error" variant="contained" onClick={() => handleDeleteScheduledClass(params.row.id)}>
            Delete
          </Button>
        </Box>
      )
    }
  ];

  const {
    columnVisibilityModel: listColumnVisibilityModel,
    setColumnVisibilityModel: setListColumnVisibilityModel,
    setFieldVisible: setListFieldVisible,
    showSimpleView: showListSimpleView,
    showFullView: showListFullView,
    resetView: resetListView
  } = useSavedColumnView('calendar-list', listColumns, ['calendar_color', 'class_code', 'class_name', 'class_date', 'class_time', 'instructor_name', 'student_count']);

  const renderDayCell = (day) => {
    const eventsForDay = calendarEvents.filter((event) =>
      isDateInRange(day, event.startDateObj, event.endDateObj)
      && (!enableRecurrence || !selectedCalendarClassId || event.class_id === selectedCalendarClassId)
    );
    const isCurrentMonth = viewMode === 'week' || day.getMonth() === currentMonth.getMonth();
    const isToday = sameDay(day, new Date());

    return (
      <Paper
        key={day.toISOString()}
        variant="outlined"
        sx={{
          ...calendarDayCellSx,
          bgcolor: isCurrentMonth ? 'background.paper' : 'action.hover',
          borderColor: isToday ? 'primary.main' : 'divider'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isToday ? 700 : 500,
            color: isCurrentMonth ? 'text.primary' : 'text.disabled',
            mb: 0.75,
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          {day.getDate()}
        </Typography>

        <Stack spacing={0.5}>
          {eventsForDay.map((event) => (
            <Box
              key={`${event.occurrenceKey || event.id}-${day.toISOString()}`}
              sx={{
                bgcolor: event.calendarColor,
                color: event.calendarTextColor,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.16)',
                px: 0.75,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              onClick={() => handleEditScheduledClass(event)}
            >
              <Typography variant="caption" display="block" sx={{ color: event.calendarTextColor, lineHeight: 1.1 }}>
                {formatTimeLabel(event.class_time)} - {formatTimeLabel(event.end_time)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: event.calendarTextColor,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  lineHeight: 1.1
                }}
              >
                {event.class_name}
              </Typography>
              {event.recurrence_label && (
                <Typography variant="caption" display="block" sx={{ color: event.calendarTextColor, lineHeight: 1.1 }}>
                  {event.recurrence_label}
                </Typography>
              )}
              {event.instructor_name && (
                <Typography variant="caption" display="block" sx={{ color: event.calendarTextColor, lineHeight: 1.1 }}>
                  {event.instructor_name}
                </Typography>
              )}
              <Typography variant="caption" display="block" sx={{ color: event.calendarTextColor, lineHeight: 1.1 }}>
                {Number(event.student_count || 0)} {Number(event.student_count || 0) === 1 ? 'student' : 'students'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            Calendar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Schedule classes, assign instructors, and assign students.
            {enableRecurrence ? ' Recurrence testing is enabled on this cloned calendar.' : ''}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          alignItems="center"
          sx={{
            '& .MuiButton-root': {
              minWidth: 'max-content',
              whiteSpace: 'nowrap'
            }
          }}
        >
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            aria-label="Calendar view mode"
          >
            <ToggleButton value="calendar" aria-label="Calendar view">
              <ViewModule fontSize="small" />
            </ToggleButton>
            <ToggleButton value="week" aria-label="Weekly calendar view">
              <ViewWeek fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewList fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button variant="contained" startIcon={<Add />} onClick={openScheduleDialog}>
            Schedule Class
          </Button>

          {viewMode !== 'list' && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="nowrap" alignItems="center">
              <Button variant="outlined" startIcon={<ChevronLeft />} onClick={viewMode === 'week' ? handlePrevWeek : handlePrevMonth}>
                Prev
              </Button>
              <Button variant="outlined" endIcon={<ChevronRight />} onClick={viewMode === 'week' ? handleNextWeek : handleNextMonth}>
                Next
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>

      {viewMode === 'list' ? (
        <Paper sx={{ p: 2 }}>
          <SavedColumnViewControls
            columns={listColumns}
            columnVisibilityModel={listColumnVisibilityModel}
            setFieldVisible={setListFieldVisible}
            showSimpleView={showListSimpleView}
            showFullView={showListFullView}
            resetView={resetListView}
          />
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={scheduledClasses}
              columns={listColumns}
              loading={loading}
              rowHeight={58}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              columnVisibilityModel={listColumnVisibilityModel}
              onColumnVisibilityModelChange={setListColumnVisibilityModel}
            />
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            mb={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonth />
              <Typography variant="h5">
                {viewMode === 'week' ? formatWeekRange(currentMonth) : formatMonthYear(currentMonth)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {showStatusFilter && (
                <TextField
                  select
                  size="small"
                  label="Class Status"
                  value={classStatusFilter}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setClassStatusFilter(value);
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => selected
                      .map((value) => CLASS_STATUS_FILTER_OPTIONS.find((option) => option.value === value)?.label || value)
                      .join(', ')
                  }}
                  sx={{ minWidth: 210 }}
                >
                  {CLASS_STATUS_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={classStatusFilter.includes(option.value)} />
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {enableRecurrence && (
                <Chip
                  label="All Classes"
                  clickable
                  variant={!selectedCalendarClassId ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCalendarClassId('')}
                  sx={{ fontWeight: 700 }}
                />
              )}
              {calendarLegendClasses.map((item) => {
                const classColor = getClassColor(item, classColorById);
                const textColor = readableTextColor(classColor);
                const selected = enableRecurrence && selectedCalendarClassId === item.id;
                return (
                  <Chip
                    key={item.id}
                    label={item.name}
                    clickable={enableRecurrence}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={enableRecurrence ? () => setSelectedCalendarClassId((current) => current === item.id ? '' : item.id) : undefined}
                    sx={{
                      bgcolor: classColor,
                      color: textColor,
                      fontWeight: 700,
                      opacity: enableRecurrence && selectedCalendarClassId && !selected ? 0.55 : 1,
                      border: selected ? '3px solid #111827' : '1px solid rgba(0, 0, 0, 0.18)',
                      cursor: enableRecurrence ? 'pointer' : 'default',
                      '& .MuiChip-label': { color: textColor }
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>

          {viewMode === 'calendar' ? (
            <Stack spacing={3}>
              {continuousCalendarMonths.map(({ month, days }, monthIndex) => (
                <Box key={`${month.getFullYear()}-${month.getMonth()}`}>
                  {monthIndex > 0 && (
                    <Typography variant="h5" sx={{ mb: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      {formatMonthYear(month)}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                      gap: 1
                    }}
                  >
                    {Array.from({ length: 7 }).map((_, dayIndex) => (
                      <Box
                        key={`day-header-${monthIndex}-${dayIndex}`}
                        sx={{
                          py: 0.75,
                          textAlign: 'center',
                          fontWeight: 700,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          fontSize: { xs: '0.85rem', md: '1rem' }
                        }}
                      >
                        {getDayName(dayIndex)}
                      </Box>
                    ))}
                    {days.map(renderDayCell)}
                  </Box>
                </Box>
              ))}
              <Box ref={calendarContinuationRef} aria-label="Load more calendar months" sx={{ height: 1 }} />
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 1
              }}
            >
              {weekDays.map((day) => (
                <Box
                  key={`day-header-${day.getDay()}`}
                  sx={{
                    py: 0.75,
                    textAlign: 'center',
                    fontWeight: 700,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    fontSize: { xs: '0.85rem', md: '1rem' }
                  }}
                >
                  {`${getDayName(day.getDay())} ${day.getDate()}`}
                </Box>
              ))}
              {weekDays.map(renderDayCell)}
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Scheduled Class' : 'Schedule Class'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {workflowStatus && <Alert severity="success" onClose={() => setWorkflowStatus('')}>{workflowStatus}</Alert>}
            {workflowError && <Alert severity="error" onClose={() => setWorkflowError('')}>{workflowError}</Alert>}
          </Stack>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Class"
                value={scheduleForm.class_id}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    class_id: e.target.value
                  }))
                }
              >
                {availableClasses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Class Status"
                value={scheduleForm.status || 'open'}
                helperText="Anyone can lock a class. Only an admin can unlock it."
                onChange={(e) => setScheduleForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="locked">Locked</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </TextField>
            </Grid>

            {editing && (scheduleForm.status === 'locked' || scheduleForm.status === 'archived') && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Administrator Override"
                  value={scheduleForm.admin_override ? 'yes' : 'no'}
                  helperText="Admins can override locked edits when needed."
                  onChange={(e) => setScheduleForm((prev) => ({ ...prev, admin_override: e.target.value === 'yes' }))}
                >
                  <MenuItem value="no">No override</MenuItem>
                  <MenuItem value="yes">Admin override</MenuItem>
                </TextField>
              </Grid>
            )}

            {enableRecurrence && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Class Length
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={scheduleForm.schedule_type || 'one_day'}
                      onChange={(_, value) => value && setScheduleForm((prev) => ({
                        ...prev,
                        schedule_type: value,
                        end_date: value === 'one_day' ? prev.class_date : prev.end_date,
                        recurrence_days: value === 'recurring' ? (prev.recurrence_days?.length ? prev.recurrence_days : [parseDbDate(prev.class_date)?.getDay()].filter((day) => day !== undefined)) : [],
                        recurrence_weeks: null
                      }))}
                    >
                      <ToggleButton value="one_day">One Day</ToggleButton>
                      <ToggleButton value="date_range">Date Range</ToggleButton>
                      <ToggleButton value="recurring">Recurring</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">
                      Use One Day for classes where the start and end date should stay the same. Date Range preserves the existing multi-day scheduling option. Recurring uses the Start Date and End Date below, then shows only the selected weekdays inside that range.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={scheduleForm.class_date}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    class_date: e.target.value,
                    end_date: enableRecurrence && prev.schedule_type === 'one_day' ? e.target.value : prev.end_date
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={scheduleForm.class_time}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    class_time: e.target.value
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={enableRecurrence && scheduleForm.schedule_type === 'one_day' ? scheduleForm.class_date : scheduleForm.end_date}
                disabled={enableRecurrence && scheduleForm.schedule_type === 'one_day'}
                helperText={enableRecurrence && scheduleForm.schedule_type === 'one_day' ? 'One-day classes end on the start date.' : ''}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    end_date: e.target.value
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={scheduleForm.end_time}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    end_time: e.target.value
                  }))
                }
              />
            </Grid>

            {enableRecurrence && scheduleForm.schedule_type === 'recurring' && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Recurring Schedule Engine
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pick the actual meeting days. Recurring schedules use the Start Date and End Date above; duration weeks are no longer used.
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <ToggleButtonGroup
                          size="small"
                          value={scheduleForm.recurrence_days || []}
                          onChange={(_, value) =>
                            setScheduleForm((prev) => ({
                              ...prev,
                              recurrence_days: normalizeRecurrenceDays(value)
                            }))
                          }
                        >
                          {WEEKDAY_OPTIONS.map((day) => (
                            <ToggleButton key={day.value} value={day.value}>
                              {day.short}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                      Generated dates: {buildRecurringMeetingDates(scheduleForm.class_date, scheduleForm.end_date, scheduleForm.recurrence_days).map(formatDate).join(', ') || 'Choose meeting days and confirm the start/end dates to generate dates.'}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} md={8}>
              <TextField
                select
                fullWidth
                label="Instructor"
                value={scheduleForm.instructor_id}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    instructor_id: e.target.value
                  }))
                }
              >
                <MenuItem value="">None</MenuItem>
                {activeInstructors.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.code ? `${item.code} - ` : ''}{item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                sx={{ height: '56px' }}
                onClick={() => setAddInstructorOpen(true)}
              >
                Add New Instructor
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={studentOptions}
                value={selectedStudentObjects}
                loading={studentSearchLoading}
                filterOptions={(x) => x}
                getOptionLabel={(option) => {
                  const name = `${option.first_name || ''} ${option.last_name || ''}`.trim() || 'Unnamed student';
                  const details = studentDisplayDetails(option).identity.slice(0, 3).join(' • ');
                  return details ? `${name} — ${details}` : name;
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, value) => {
                  setStudentSearch(value);
                }}
                onChange={(_, value) => {
                  const existingStudents = editing
                    ? selectedStudentObjects.filter((student) => originalStudentIds.includes(student.id))
                    : [];
                  const nextById = new Map(existingStudents.map((student) => [student.id, student]));
                  value.forEach((student) => nextById.set(student.id, student));
                  const next = [...nextById.values()];
                  setSelectedStudentObjects(next);
                  setStudentsDirty(true);
                  setScheduleForm((prev) => ({
                    ...prev,
                    student_ids: next.map((student) => student.id)
                  }));
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and Add Students"
                    placeholder="Type name, code, last 4 SSN, DOB, phone, email, or driver number"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {studentSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const name = `${option.first_name || ''} ${option.middle_name || ''} ${option.last_name || ''}`.replace(/\s+/g, ' ').trim() || 'Unnamed student';
                  const { identity: details, address } = studentDisplayDetails(option);

                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" fontWeight={700}>
                          {name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {details.join(' • ') || 'No identifying details'}
                        </Typography>
                        {address && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {address}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
              {editing && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Existing enrollments are preserved here. Add students in Calendar; move individual students from the Roster.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2">
                    Selected Students ({selectedStudentObjects.length})
                  </Typography>
                </Box>
                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    bgcolor: 'background.default'
                  }}
                >
                  {selectedStudentObjects.length ? (
                    <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
                      {selectedStudentObjects.map((student) => {
                        const name = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.replace(/\s+/g, ' ').trim() || 'Unnamed Student';
                        const { identity, address } = studentDisplayDetails(student);
                        const meta = identity.join(' • ');
                        const hasPayment = Boolean(student.has_payment)
                          || Number(student.payment_count || 0) > 0
                          || Number(student.payment_total || 0) > 0;
                        const isExistingEnrollment = Boolean(editing)
                          && originalStudentIds.includes(student.id);
                        return (
                          <Box
                            key={student.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                              px: 2,
                              py: 1.25,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>{name}</Typography>
                              {meta && (
                                <Typography variant="caption" color="text.secondary" display="block">{meta}</Typography>
                              )}
                              {address && (
                                <Typography variant="caption" color="text.secondary" display="block">{address}</Typography>
                              )}
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                              <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => openStudentProfile(student)}>
                                Edit Profile
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color={hasPayment ? 'inherit' : 'primary'}
                                startIcon={<Payments />}
                                onClick={() => openStudentPayment(student)}
                                sx={hasPayment ? {
                                  color: 'text.secondary',
                                  borderColor: 'grey.400',
                                  bgcolor: 'grey.100',
                                  '&:hover': {
                                    bgcolor: 'grey.200',
                                    borderColor: 'grey.500'
                                  }
                                } : undefined}
                                title={hasPayment ? 'Payment already taken — click to add another payment if needed.' : 'Take payment'}
                              >
                                Take Payment
                              </Button>
                              {isExistingEnrollment ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                  title="Move enrolled students from the Roster, where each move is individually audited."
                                >
                                  Manage in Roster
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const next = selectedStudentObjects.filter((s) => s.id !== student.id);
                                    setSelectedStudentObjects(next);
                                    setStudentsDirty(true);
                                    setScheduleForm((prev) => ({
                                      ...prev,
                                      student_ids: next.map((s) => s.id)
                                    }));
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.paper' }}>
                      <Typography variant="body2" color="text.secondary">
                        No students selected yet. Search above to add students to this class.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => setAddStudentOpen(true)}
              >
                Add New Student
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                value={scheduleForm.location}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    location: e.target.value
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notes"
                value={scheduleForm.notes}
                onChange={(e) =>
                  setScheduleForm((prev) => ({
                    ...prev,
                    notes: e.target.value
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveScheduledClass}>
            Save Class
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addInstructorOpen} onClose={() => setAddInstructorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Instructor</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Code"
            value={newInstructor.code}
            onChange={(e) => setNewInstructor({ ...newInstructor, code: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={newInstructor.name}
            onChange={(e) => setNewInstructor({ ...newInstructor, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Phone"
            value={newInstructor.phone}
            onChange={(e) => setNewInstructor({ ...newInstructor, phone: formatPhone(e.target.value) })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            value={newInstructor.email}
            onChange={(e) => setNewInstructor({ ...newInstructor, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddInstructorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInstructor}>
            Save Instructor
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addStudentOpen} onClose={() => setAddStudentOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Create Student</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {workflowStatus && <Alert severity="success" onClose={() => setWorkflowStatus('')}>{workflowStatus}</Alert>}
            {workflowError && <Alert severity="error" onClose={() => setWorkflowError('')}>{workflowError}</Alert>}
          </Stack>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Code" value={newStudent.code} onChange={(e) => setNewStudent({ ...newStudent, code: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="SSN Last 4"
                value={newStudent.ssn}
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                helperText="Enter last four digits only."
                onChange={(e) => setNewStudent({ ...newStudent, ssn: formatSSNLast4(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Driver Number" value={newStudent.driver_number} onChange={(e) => setNewStudent({ ...newStudent, driver_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Birth Date" type="date" InputLabelProps={{ shrink: true }} value={newStudent.birth_date} onChange={(e) => setNewStudent({ ...newStudent, birth_date: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth label="First Name" value={newStudent.first_name} onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Middle Name" value={newStudent.middle_name} onChange={(e) => setNewStudent({ ...newStudent, middle_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Last Name" value={newStudent.last_name} onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Address" value={newStudent.address} onChange={(e) => setNewStudent({ ...newStudent, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="City" value={newStudent.city} onChange={(e) => setNewStudent({ ...newStudent, city: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField fullWidth label="State" value={newStudent.state} onChange={(e) => setNewStudent({ ...newStudent, state: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField fullWidth label="ZIP" value={newStudent.zip} onChange={(e) => setNewStudent({ ...newStudent, zip: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Phone" value={newStudent.phone} onChange={(e) => setNewStudent({ ...newStudent, phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Cause Number" value={newStudent.cause_number} onChange={(e) => setNewStudent({ ...newStudent, cause_number: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="County of Conviction" value={newStudent.county_of_conviction} onChange={(e) => setNewStudent({ ...newStudent, county_of_conviction: e.target.value })} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Probation Officer" value={newStudent.probation_officer_name} onChange={(e) => setNewStudent({ ...newStudent, probation_officer_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Probation Email" value={newStudent.probation_officer_email} onChange={(e) => setNewStudent({ ...newStudent, probation_officer_email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Probation Phone" value={newStudent.probation_officer_phone} onChange={(e) => setNewStudent({ ...newStudent, probation_officer_phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Attorney" value={newStudent.attorney_name} onChange={(e) => setNewStudent({ ...newStudent, attorney_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Attorney Email" value={newStudent.attorney_email} onChange={(e) => setNewStudent({ ...newStudent, attorney_email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Attorney Phone" value={newStudent.attorney_phone} onChange={(e) => setNewStudent({ ...newStudent, attorney_phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Referral Source" value={newStudent.referral_source} onChange={(e) => setNewStudent({ ...newStudent, referral_source: e.target.value })}>
                <MenuItem value="">Not selected</MenuItem>
                {referralSourceOptionsFor(newStudent.referral_source).map((source) => (
                  <MenuItem key={source} value={source}>{source}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Additional State Reporting Fields" value={newStudent.state_reporting_notes} onChange={(e) => setNewStudent({ ...newStudent, state_reporting_notes: e.target.value })} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Reporting Agency" value={newStudent.reporting_agency} onChange={(e) => setNewStudent({ ...newStudent, reporting_agency: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Call Notes" value={newStudent.call_notes} onChange={(e) => setNewStudent({ ...newStudent, call_notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStudentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateStudent}>
            Save Student
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={studentProfileOpen} onClose={() => setStudentProfileOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Student Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}><TextField fullWidth label="Code" value={studentProfileForm.code} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, code: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="SSN Last 4" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={studentProfileForm.ssn} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, ssn: formatSSNLast4(e.target.value) })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Driver Number" value={studentProfileForm.driver_number} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, driver_number: e.target.value })} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Birth Date" type="date" InputLabelProps={{ shrink: true }} value={studentProfileForm.birth_date} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, birth_date: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="First Name" value={studentProfileForm.first_name} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, first_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Middle Name" value={studentProfileForm.middle_name} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, middle_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Last Name" value={studentProfileForm.last_name} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, last_name: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Address" value={studentProfileForm.address} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, address: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="City" value={studentProfileForm.city} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, city: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="State" value={studentProfileForm.state} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, state: e.target.value })} /></Grid>
            <Grid item xs={12} md={2}><TextField fullWidth label="ZIP" value={studentProfileForm.zip} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, zip: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Phone" value={studentProfileForm.phone} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, phone: formatPhone(e.target.value) })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email" value={studentProfileForm.email} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Cause Number" value={studentProfileForm.cause_number} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, cause_number: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="County of Conviction" value={studentProfileForm.county_of_conviction} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, county_of_conviction: e.target.value })} /></Grid>

            <Grid item xs={12}><TextField fullWidth label="Probation Officer" value={studentProfileForm.probation_officer_name} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, probation_officer_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Probation Email" value={studentProfileForm.probation_officer_email} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, probation_officer_email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Probation Phone" value={studentProfileForm.probation_officer_phone} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, probation_officer_phone: formatPhone(e.target.value) })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney" value={studentProfileForm.attorney_name} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, attorney_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney Email" value={studentProfileForm.attorney_email} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, attorney_email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Attorney Phone" value={studentProfileForm.attorney_phone} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, attorney_phone: formatPhone(e.target.value) })} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Referral Source" value={studentProfileForm.referral_source} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, referral_source: e.target.value })}>
                <MenuItem value="">Not selected</MenuItem>
                {referralSourceOptionsFor(studentProfileForm.referral_source).map((source) => (
                  <MenuItem key={source} value={source}>{source}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Additional State Reporting Fields" value={studentProfileForm.state_reporting_notes} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, state_reporting_notes: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Reporting Agency" value={studentProfileForm.reporting_agency} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, reporting_agency: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Call Notes" value={studentProfileForm.call_notes} onChange={(e) => setStudentProfileForm({ ...studentProfileForm, call_notes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentProfileOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={studentProfileSaving} onClick={saveStudentProfile}>
            Save Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={studentPaymentOpen} onClose={() => setStudentPaymentOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Take Payment</DialogTitle>
        <DialogContent>
          {studentPaymentTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {`${studentPaymentTarget.first_name || ''} ${studentPaymentTarget.last_name || ''}`.trim() || 'Selected Student'}
                {editing?.class_name ? ` • ${editing.class_name}` : ''}
              </Typography>
              <TextField fullWidth required label="Amount" type="number" value={studentPaymentForm.amount} onChange={(e) => setStudentPaymentForm({ ...studentPaymentForm, amount: e.target.value })} helperText={studentPaymentForm.amount ? formatMoney(studentPaymentForm.amount) : ''} />
              <TextField fullWidth label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={studentPaymentForm.payment_date} onChange={(e) => setStudentPaymentForm({ ...studentPaymentForm, payment_date: e.target.value })} />
              <TextField fullWidth label="Method" value={studentPaymentForm.method} onChange={(e) => setStudentPaymentForm({ ...studentPaymentForm, method: e.target.value })} />
              <TextField fullWidth label="Reference" value={studentPaymentForm.reference_number} onChange={(e) => setStudentPaymentForm({ ...studentPaymentForm, reference_number: e.target.value })} />
              <TextField fullWidth multiline minRows={2} label="Notes" value={studentPaymentForm.notes} onChange={(e) => setStudentPaymentForm({ ...studentPaymentForm, notes: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentPaymentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveStudentPayment}>Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}